import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CostCalculatorSection = () => {
  const [selectedSolution, setSelectedSolution] = useState('solar');
  const [houseSize, setHouseSize] = useState(150);
  const [energyUsage, setEnergyUsage] = useState(3000);

  const solutions = [
    { id: 'solar', name: 'Zonnepanelen', icon: 'Sun', costPerUnit: 250, unit: 'm²' },
    { id: 'heatpump', name: 'Warmtepomp', icon: 'Thermometer', costPerUnit: 12000, unit: 'systeem' },
    { id: 'insulation', name: 'Isolatie', icon: 'Home', costPerUnit: 50, unit: 'm²' },
    { id: 'battery', name: 'Thuisbatterij', icon: 'Battery', costPerUnit: 800, unit: 'kWh' }
  ];

  const calculateCost = () => {
    const solution = solutions?.find(s => s?.id === selectedSolution);
    if (selectedSolution === 'solar') {
      return Math.round((houseSize / 10) * solution?.costPerUnit);
    } else if (selectedSolution === 'heatpump') {
      return solution?.costPerUnit;
    } else if (selectedSolution === 'insulation') {
      return Math.round(houseSize * solution?.costPerUnit);
    } else if (selectedSolution === 'battery') {
      return Math.round((energyUsage / 365 / 2) * solution?.costPerUnit);
    }
    return 0;
  };

  const calculateSavings = () => {
    if (selectedSolution === 'solar') {
      return Math.round(energyUsage * 0.25 * 0.30);
    } else if (selectedSolution === 'heatpump') {
      return Math.round(energyUsage * 0.40 * 0.30);
    } else if (selectedSolution === 'insulation') {
      return Math.round(energyUsage * 0.20 * 0.30);
    } else if (selectedSolution === 'battery') {
      return Math.round(energyUsage * 0.15 * 0.30);
    }
    return 0;
  };

  const totalCost = calculateCost();
  const yearlySavings = calculateSavings();
  const paybackPeriod = yearlySavings > 0 ? Math.round(totalCost / yearlySavings) : 0;

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Interactieve kostencalculator
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Bereken direct wat uw verduurzaming kost en wanneer het zich terugverdient
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-elevation-2"
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Selecteer uw oplossing</h3>
            
            {/* Solution Selection */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {solutions?.map((solution) => (
                <motion.button
                  key={solution?.id}
                  onClick={() => setSelectedSolution(solution?.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedSolution === solution?.id
                      ? 'border-primary bg-primary/5 shadow-elevation-1'
                      : 'border-gray-200 bg-white hover:border-primary/50'
                  }`}
                >
                  <Icon name={solution?.icon} size={32} className={selectedSolution === solution?.id ? 'text-primary' : 'text-muted-foreground'} />
                  <p className="text-sm font-semibold mt-2">{solution?.name}</p>
                </motion.button>
              ))}
            </div>

            {/* House Size Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground mb-3">
                Woninggrootte: {houseSize} m²
              </label>
              <input
                type="range"
                min="50"
                max="300"
                value={houseSize}
                onChange={(e) => setHouseSize(Number(e?.target?.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>50 m²</span>
                <span>300 m²</span>
              </div>
            </div>

            {/* Energy Usage Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground mb-3">
                Jaarlijks energieverbruik: {energyUsage} kWh
              </label>
              <input
                type="range"
                min="1000"
                max="8000"
                step="100"
                value={energyUsage}
                onChange={(e) => setEnergyUsage(Number(e?.target?.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>1.000 kWh</span>
                <span>8.000 kWh</span>
              </div>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 shadow-elevation-2"
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Uw berekening</h3>

            {/* Total Cost */}
            <motion.div
              key={totalCost}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl p-6 mb-4 shadow-elevation-1"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground">Totale investering</span>
                <Icon name="Euro" size={20} className="text-primary" />
              </div>
              <p className="text-4xl font-bold text-foreground">€{totalCost?.toLocaleString()}</p>
            </motion.div>

            {/* Yearly Savings */}
            <motion.div
              key={yearlySavings}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-white rounded-xl p-6 mb-4 shadow-elevation-1"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground">Jaarlijkse besparing</span>
                <Icon name="TrendingDown" size={20} className="text-success" />
              </div>
              <p className="text-4xl font-bold text-success">€{yearlySavings?.toLocaleString()}</p>
            </motion.div>

            {/* Payback Period */}
            <motion.div
              key={paybackPeriod}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-white rounded-xl p-6 mb-6 shadow-elevation-1"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground">Terugverdientijd</span>
                <Icon name="Clock" size={20} className="text-primary" />
              </div>
              <p className="text-4xl font-bold text-primary">{paybackPeriod} jaar</p>
            </motion.div>

            {/* CTA */}
            <Button size="lg" className="w-full" iconName="FileText" iconPosition="left">
              Vraag offerte aan
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CostCalculatorSection;